/**
 * **打印节点的参数表** —— 参数的**单一来源**。
 *
 * ## 为什么要有这张表
 *
 * 一个参数在三个地方出现，而以前是三份各自手写的：
 *
 * ```text
 *   ① 节点上的行（label / 控件类型 / 值域 / 单位）   ← nodes.ts
 *   ② 侧栏面板上的字段（同一套 label / 值域 / 单位）  ← panels.ts
 *   ③ 几何与求值读哪个 id                            ← pageModel.ts / evaluators.ts
 * ```
 *
 * ① 和 ② 重复写一遍的代价不是"多打几个字"，而是**它们会漂**：
 * 节点上写「X 维度 min 100 max 2000」，面板上写 `min: 100`（少个 0），
 * 于是同一个参数在画布上拖不动、在面板里能填 —— 而两边都不报错。
 * 这一轮之前已经有这种苗头（面板自己 `Math.round(v*100)/100`，
 * 而节点控件的精度写在 `widget.precision`）。
 *
 * 所以：**表里写一次**，节点行、面板字段都从它生成。
 *
 * ## 分组（`group`）——「参数抽象」
 *
 * 每个参数归一个**组**（布局 / 内容 / 字体 / 外观 / 编码 / 可读文本 / 纸张 / 输出），
 * 组在节点上变成可折叠的分组行（`node/rows.ts` 的 `resolveSections`），
 * 在面板上变成小标题。判据是**用户的心智**而不是数据结构：
 *
 *   · 「布局」= 它在纸上的哪儿、多大、转多少 —— 每个组件都有，只是成员不同
 *     （条码没有"宽"，二维码没有独立的"高"）；
 *   · 「内容」= 印出来的字；「字体」= 这些字长什么样；
 *   · 「编码」= 条码/二维码专有的那些（符号、X 维度、纠错等级）；
 *   · 「外观」= 线宽 / 填充 / 圆角 / 反白。
 *
 * ⚠️ 分组行的 `match` 是正则、按**行 id** 归集，而这里**由成员 id 生成**
 * （`sectionOf`）—— 手写正则迟早和成员列表对不上，而对不上的表现是
 * 「某个参数突然跑到节点外面去了」，那种错没人会去查正则。
 */
import type { NodeRow } from '@/types/node'
import type { SocketType } from '@/types/socket'
import type { Widget } from '@/types/widget'
import { DEFAULT_JOB } from './job'
import { DEFAULT_PAPER } from './units'

/* ══════════════════════════════════════════════════════════════════
   组件种类与分组
   ══════════════════════════════════════════════════════════════════ */

/** 参数挂在哪种节点上 */
export type ParamComponent =
  /* 文档 / 页面是"另一种东西"：一份文档（含纸张）里若干张纸 */
  | 'document'
  | 'page'
  | 'box'
  | 'text'
  | 'image'
  | 'code'
  | 'barcode'
  | 'qrcode'

/**
 * ★★ **组件之间的扩展关系**（参数沿这条链累加）。
 *
 * ```text
 *   矩形 (box) ─┬── 文本 (text) ──── 编码 (code) ──┬── 条码 (barcode)
 *               │                                  └── 二维码 (qrcode)
 *               └── 图片 (image)：只要绘制，不要文字
 * ```
 *
 * 这条链是**用户自己说的**，而且它比"每个组件各列一份参数"准确：
 *   · 矩形 = 布局 + 外观（边框 + 背景）—— 它**没有内容**；
 *   · 文本 = 矩形 + 内容 + 字体；
 *   · 编码（`code`，**抽象节点，没有对应的节点类型**）= 文本 + 编码参数
 *     （符号 / 模块尺寸）—— 一维和二维都要的那几项；
 *   · 条码 = 编码 + HRI；二维码 = 编码 + 纠错等级；
 *   · 图片 = 矩形 + 图像源（不需要文字）。
 *
 * ⚠️ `code` 这一层是**被测试逼出来的**：一开始把「符号」挂在条码上，
 * 于是二维码节点的参数里**没有符号**（它的链是 qrcode→text→box）——
 * 而二维码当然要选符号。抽象出中间这一层之后，"一维二维共有的"和
 * "只有一维 / 只有二维的"就各就各位了。
 *
 * 好处不只是少写几行：新加一个组件 = 声明"它从谁扩展"+ 它自己那几个参数，
 * 其余全部继承。而"文本有的条码没有"这种漂移从结构上就不可能发生。
 */
export const COMPONENT_BASE: Record<ParamComponent, ParamComponent | null> = {
  /* 页面 / 文档是另一种东西（一张纸 vs 一整份），不从组件链上扩展 */
  document: null,
  page: null,
  box: null,
  text: 'box',
  /* 图片从**矩形**扩展（只要绘制，不要文字）—— 用户给的链 */
  image: 'box',
  code: 'text',
  barcode: 'code',
  qrcode: 'code',
}

/** 自己 + 全部祖先（从自己往上） */
export function componentChain(kind: ParamComponent): ParamComponent[] {
  const chain: ParamComponent[] = []
  let cursor: ParamComponent | null = kind
  while (cursor) {
    chain.push(cursor)
    cursor = COMPONENT_BASE[cursor]
  }
  return chain
}

/** 参数分组（节点上是可折叠段，面板上是小标题） */
export type ParamGroupId =
  | 'content'
  | 'layout'
  | 'font'
  | 'appearance'
  | 'encode'
  | 'hri'
  | 'job'
  | 'paper'
  | 'output'

export interface ParamGroupSpec {
  id: ParamGroupId
  label: string
  /** 一段说明（面板里显示在标题旁边，鼠标悬停） */
  hint?: string
  /**
   * 默认展开吗？**不写就是收起**。
   *
   * ⚠️ 默认收起是刻意的：一个条码节点展开之后有十来行，全部铺开会把
   * 节点撑得比屏幕还高，拖动和连线的手感全毁了（真机反馈）。
   * 只有"用户一放下来就要改"的那一组（内容）才标 `expanded: true`。
   */
  expanded?: boolean
}

/**
 * 分组的**顺序**（节点从上到下、面板从上到下）。
 *
 * ⚠️ **没有「布局」组**：位置 / 尺寸 / 旋转是**重点参数**，它们
 * `group: null` —— 直接列在节点上（不进折叠段）。理由是这几行**每次都要看**：
 * 放进折叠段里，用户每改一次位置都要先展开一次；而它们总共 5 行，
 * 铺开的代价远小于"每次多两下点击"。
 *
 * ⚠️ 其余分组**默认收起**（`expanded` 不写就是收起），只有「内容」默认展开：
 * 那是一个组件放下来之后第一个要改的东西。
 */
export const PARAM_GROUPS: readonly ParamGroupSpec[] = [
  { id: 'content', label: '内容', expanded: true },
  { id: 'font', label: '字体' },
  { id: 'appearance', label: '外观', hint: '边框与背景' },
  { id: 'encode', label: '编码', hint: '符号与模块尺寸' },
  { id: 'job', label: '打印作业', hint: '份数与机器动作' },
  { id: 'hri', label: '可读文本', hint: '条码下面那行字（算在总高之内）' },
  { id: 'paper', label: '纸张' },
  { id: 'output', label: '输出' },
]

/* ══════════════════════════════════════════════════════════════════
   控件描述
   ══════════════════════════════════════════════════════════════════ */

/**
 * 一个参数**怎么编辑**。
 *
 * ⚠️ 这是"编辑方式"的描述，不是值本身 —— 值在文档里（每个节点实例一份）。
 * 面板与节点行都从这里生成控件，所以「值域 / 单位 / 精度」只有这一份。
 */
export type ParamEditor =
  /** 长度（mm）：precision 2、unit mm */
  | { kind: 'mm'; value: number; min?: number; max?: number; step?: number }
  /** 微米（整数）：只给条码的 X 维度用 */
  | { kind: 'micron'; value: number; min?: number; max?: number; step?: number }
  /** 纯数值（无单位，或自带 unit 文案） */
  | { kind: 'number'; value: number; min?: number; max?: number; step?: number; precision?: number; unit?: string }
  | { kind: 'text'; value: string; multiline?: boolean; rows?: number }
  /**
   * 颜色（`#rrggbb`）。
   *
   * ⚠️ 热敏打印机只有"上墨 / 不上墨"两态（单色）：颜色在**编辑与预览**里
   * 是真的，在 1-bit 输出里由执行侧决定怎么落（多数情况是非白即墨）。
   * 多色机（黑 + 红双色带）才会真的按颜色分版 —— 那属于输出侧的事，
   * IR 只把颜色如实带过去（见 `type-contract` §8.1）。
   */
  | { kind: 'color'; value: string }
  /**
   * **图像**：值是内嵌数据（`data:image/…;base64,…`，空串 = 还没有图）。
   *
   * ⚠️ 不是"图片地址"：设计器离线（见 `types/widget.ts` 的 `ImageWidget`）。
   */
  | { kind: 'image'; value: string }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'enum'; value: string | number; options: readonly { label: string; value: string | number }[] }

export interface ParamSpec {
  /** 行 id —— **文档里的键**（存档、求值、面板都按它读；改名 = breaking change） */
  id: string
  label: string
  /**
   * 归哪个分组；`null` = **重点参数**（不进折叠段，直接列在节点上）。
   *
   * 判据是"用户每改一次要不要先展开"：位置 / 尺寸 / 旋转显然不要，
   * 编码细节和字体细节要。
   */
  group: ParamGroupId | null
  editor: ParamEditor
  /**
   * **引入这个参数的组件**（谁"扩展"出来的）。
   *
   * ⚠️ 不是"哪些组件有这个参数"：后者要手写一遍列表，加了组件就得回来补，
   * 忘了就是"文本有的条码没有"。沿 `COMPONENT_BASE` 往上取链才是单一来源。
   */
  owner: ParamComponent
  /**
   * 有端口 = 这一行是**输入行**（可以接线，值从上游来）。
   * 没有 = 属性行（只有内联控件，摆位置这类设计期参数就该是这样）。
   */
  socket?: SocketType
  hint?: string
}

/* ══════════════════════════════════════════════════════════════════
   ★ 参数表
   ══════════════════════════════════════════════════════════════════ */

/* ⚠️ 不再需要"哪些组件有这个参数"那种列表了：归属写在每个参数的 `owner` 上，
   能不能用由 `COMPONENT_BASE` 那条继承链算出来（见 `paramsFor`）。 */

export const PARAMS: readonly ParamSpec[] = [
  /* ── 内容 ─────────────────────────────────────────────────── */
  {
    id: 'text',
    label: '内容',
    group: 'content',
    owner: 'text',
    socket: 'text',
    editor: { kind: 'text', value: '文本', multiline: true, rows: 2 },
    hint: '也可以接线',
  },

  /*
   * ── 重点参数：位置 / 尺寸 / 旋转（`group: null` = 不进折叠段） ──
   *
   * ⚠️ **所有组件都有 X / Y / 宽 / 高**（用户要求，而且这对"从矩形扩展"
   * 这条链是自洽的：几何是基类的东西）。
   *
   * · 文本 / 矩形 / 图片：宽高就是框；
   * · 条码：宽 = **目标宽度**（0 = 按 X 维度自动），高 = **总高（含 HRI）**；
   *   模块宽由"目标宽度 ÷ 模块数"取整得到 —— 所以换符号、改内容都不会
   *   画出框外（真机反馈的那条）；
   * · 二维码：正方形，宽高写哪个都行（两个都写时以宽为准）。
   *
   * ⚠️ 允许**负数**：贴边 / 出血的组件本来就该能摆到纸外（真机反馈
   * "只能在 0 的位置"）。越界的部分打不出来，界面用提示说清楚，
   * 而不是把值夹在 0 上让用户够不着。
   */
  {
    id: 'xMm',
    label: 'X',
    group: null,
    owner: 'box',
    editor: { kind: 'mm', value: 8, min: -1000, max: 1000, step: 0.1 },
  },
  {
    id: 'yMm',
    label: 'Y',
    group: null,
    owner: 'box',
    editor: { kind: 'mm', value: 8, min: -1000, max: 1000, step: 0.1 },
  },
  {
    id: 'wMm',
    label: '宽',
    group: null,
    owner: 'box',
    editor: { kind: 'mm', value: 40, min: 0, max: 1000, step: 0.1 },
    hint: 'mm；0 = 自动',
  },
  {
    id: 'hMm',
    label: '高',
    group: null,
    owner: 'box',
    editor: { kind: 'mm', value: 10, min: 0, max: 1000, step: 0.1 },
    hint: 'mm；含下面的可读文本',
  },
  {
    id: 'rotationDeg',
    label: '旋转',
    group: null,
    owner: 'box',
    editor: {
      kind: 'enum',
      value: 0,
      options: [
        { label: '0°', value: 0 },
        { label: '90°', value: 90 },
        { label: '180°', value: 180 },
        { label: '270°', value: 270 },
      ],
    },
    hint: '只旋转画出来的内容',
  },

  /* ── 图像（图片节点） ─────────────────────────────────────── */
  {
    id: 'src',
    label: '图像',
    group: 'content',
    owner: 'image',
    socket: 'text',
    /*
     * ⚠️ **图像控件**（`kind: 'image'`），不是文本框：值是一段 `data:` URI ——
     * 上传一张图就地转成 base64 存进文档。真机反馈："把图片做成一个基础类型？
     * 比如 base64 编码的文本…增加一个上传按钮来上传一个图片，把它转成编码；
     * 不建议使用 URL，因为这需要解析代码去做 URL 访问功能"。
     */
    editor: { kind: 'image', value: '' },
    hint: '上传后内嵌进文档（不联网）',
  },
  {
    id: 'fit',
    label: '缩放方式',
    group: 'appearance',
    owner: 'image',
    editor: {
      kind: 'enum',
      value: 'contain',
      options: [
        { label: '完整放入', value: 'contain' },
        { label: '裁切填满', value: 'cover' },
        { label: '拉伸', value: 'fill' },
      ],
    },
    hint: '比例和框不一致时怎么处理',
  },
  {
    id: 'threshold',
    label: '二值化阈值',
    group: 'appearance',
    owner: 'image',
    editor: { kind: 'number', value: 128, min: 0, max: 255, step: 1, precision: 0, unit: '' },
    hint: '0–255；亮于此值的点算白',
  },

  /* ── 字体（文本） ─────────────────────────────────────────── */
  {
    id: 'sizeMm',
    label: '字号',
    group: 'font',
    owner: 'text',
    editor: { kind: 'mm', value: 3, min: 0.5, max: 50, step: 0.5 },
    hint: '字高（mm）',
  },
  {
    id: 'bold',
    label: '加粗',
    group: 'font',
    owner: 'text',
    editor: { kind: 'boolean', value: false },
  },
  {
    id: 'align',
    label: '水平对齐',
    group: 'font',
    owner: 'text',
    editor: {
      kind: 'enum',
      value: 'left',
      options: [
        { label: '左', value: 'left' },
        { label: '居中', value: 'center' },
        { label: '右', value: 'right' },
      ],
    },
  },
  {
    id: 'vAlign',
    label: '垂直对齐',
    group: 'font',
    owner: 'text',
    editor: {
      kind: 'enum',
      value: 'top',
      options: [
        { label: '上', value: 'top' },
        { label: '居中', value: 'middle' },
        { label: '下', value: 'bottom' },
      ],
    },
    hint: '文字贴在框的哪一边',
  },
  {
    id: 'color',
    label: '颜色',
    group: 'appearance',
    /*
     * ⚠️ 归 **`text`**（文本 → 编码 → 条码 / 二维码这条链），**不是** `box`：
     *   · 文本 → **字体颜色**；
     *   · 条码 / 二维码 → **前景色**（条与模块的颜色）；
     *   · **矩形用不到它**（矩形的"颜色"是边框色与背景色）；
     *   · **图像也用不到**（它是位图，颜色由图片自己决定）。
     * 真机反馈："前景色改为颜色，像文本这些可以直接用颜色作为字体颜色，
     * 条码和二维码可以用颜色来绘制前景色，矩形用不到颜色，所以图像节点应该也不用颜色"。
     */
    owner: 'text',
    editor: { kind: 'color', value: '#000000' },
    hint: '文本 = 字色；条码 / 二维码 = 前景色（单色机非白即墨）',
  },
  {
    id: 'lineHeightMm',
    label: '行距',
    group: 'font',
    owner: 'text',
    editor: { kind: 'mm', value: 0, min: 0, max: 100, step: 0.5 },
    hint: 'mm；0 = 自动',
  },

  /* ── 外观 ─────────────────────────────────────────────────── */
  {
    id: 'strokeColor',
    label: '边框颜色',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'color', value: '#000000' },
    hint: '多色带机器才会按颜色分版',
  },
  {
    id: 'fillColor',
    label: '背景颜色',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'color', value: '#ffffff' },
    hint: '打开「填充」才画底色（不填充 = 透明）',
  },
  {
    id: 'lineWidthMm',
    label: '线宽',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'mm', value: 0.3, min: 0, max: 5, step: 0.1 },
  },
  {
    id: 'fill',
    label: '填充',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'boolean', value: false },
  },
  {
    id: 'radiusMm',
    label: '圆角',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'mm', value: 0, min: 0, max: 50, step: 0.5 },
    hint: '0 = 直角',
  },
  {
    id: 'opacity',
    label: '不透明度',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'number', value: 1, min: 0, max: 1, step: 0.05, precision: 2 },
    hint: '0–1；能否打印取决于机型',
  },
  {
    id: 'invert',
    label: '反白',
    group: 'appearance',
    owner: 'box',
    editor: { kind: 'boolean', value: false },
    hint: '黑底白字',
  },

  /* ── 编码（条码 / 二维码） ────────────────────────────────── */
  {
    id: 'symbology',
    label: '符号',
    group: 'encode',
    /* ⚠️ 归**抽象层** `code`：一维二维都要它（挂条码上二维码就没有了） */
    owner: 'code',
    editor: {
      kind: 'enum',
      value: 'code128',
      /* ⚠️ 真实的选项按节点种类过滤（见 `paramsFor`）—— 一维/二维是两个节点 */
      options: [],
    },
  },
  {
    id: 'xDimMicron',
    label: 'X 维度',
    group: 'encode',
    owner: 'code',
    editor: { kind: 'micron', value: 375, min: 100, max: 2000, step: 10 },
    hint: '微米；实际会吸到整数个设备点',
  },
  {
    id: 'ecLevel',
    label: '纠错等级',
    group: 'encode',
    owner: 'qrcode',
    editor: {
      kind: 'enum',
      value: 'M',
      options: [
        { label: 'L（7%）', value: 'L' },
        { label: 'M（15%）', value: 'M' },
        { label: 'Q（25%）', value: 'Q' },
        { label: 'H（30%）', value: 'H' },
      ],
    },
    hint: '越高越抗污损，码也更密',
  },

  /* ── 可读文本（HRI） ──────────────────────────────────────── */
  {
    id: 'showHri',
    label: '显示',
    group: 'hri',
    owner: 'barcode',
    editor: { kind: 'boolean', value: true },
  },

  /* ── 纸张（页面节点） ─────────────────────────────────────── */
  {
    id: 'widthMm',
    label: '宽',
    group: 'paper',
    /* ⚠️ 纸张归**文档**（真机反馈：页面节点的纸张宽高搬到文档的整体属性） */
    owner: 'document',
    /*
     * ⚠️ 默认值**引用 `DEFAULT_PAPER`**，不在这里另写一份数字：
     * 第一版这里写死 100×150，而 `units.ts` 里是另一份 —— 于是
     * "新建文档"（控件默认值）和"没有纸张时的兜底"（`resolvedPaperOf`）
     * 给出的尺寸不一致（真机反馈把默认改成 60×30 时，只改了其中一个，
     * 表现就是新建出来的纸还是 100×150）。
     */
    editor: { kind: 'mm', value: DEFAULT_PAPER.widthMm, min: 5, max: 1000, step: 1 },
  },
  {
    id: 'heightMm',
    label: '高',
    group: 'paper',
    owner: 'document',
    editor: { kind: 'mm', value: DEFAULT_PAPER.heightMm, min: 5, max: 1000, step: 1 },
  },
  {
    id: 'backgroundColor',
    label: '预览背景',
    group: 'paper',
    owner: 'document',
    editor: { kind: 'color', value: '#ffffff' },
    hint: '仅编辑器与预览',
  },
  {
    id: 'marginMm',
    label: '边距',
    group: 'paper',
    owner: 'document',
    editor: { kind: 'mm', value: DEFAULT_PAPER.marginMm, min: 0, max: 20, step: 0.5 },
    hint: 'mm；内容不该越过的边界',
  },

  /* ── 输出（页面节点） ─────────────────────────────────────── */
  /*
   * ── 打印作业（文档级：一台机器一次只有一套介质与浓度）──
   *
   * ⚠️ 默认值一律**引用 `DEFAULT_JOB`**，不在这里另写数字 —— 和纸张同一条规矩：
   * 两处各写一份，改了一处就会"新建的文档"和"兜底"不一致。
   */
  {
    id: 'copies',
    label: '份数',
    group: 'job',
    owner: 'document',
    editor: { kind: 'number', value: DEFAULT_JOB.copies, min: 1, max: 999, step: 1, precision: 0 },
    hint: '同一份内容打几遍',
  },
  {
    id: 'cut',
    label: '切刀',
    group: 'job',
    owner: 'document',
    editor: {
      kind: 'enum',
      value: DEFAULT_JOB.cut,
      options: [
        { label: '不切', value: 'none' },
        { label: '每张切', value: 'after-each' },
        { label: '打完切', value: 'after-job' },
      ],
    },
    hint: '执行侧翻成它那台机器的切刀指令',
  },
  {
    id: 'darkness',
    label: '浓度',
    group: 'job',
    owner: 'document',
    editor: { kind: 'number', value: DEFAULT_JOB.darkness, min: 0, max: 30, step: 1, precision: 0 },
    hint: '0–30 相对值（各机型量程不同，执行侧映射）',
  },
  {
    id: 'speed',
    label: '速度',
    group: 'job',
    owner: 'document',
    editor: {
      kind: 'enum',
      value: DEFAULT_JOB.speed,
      options: [
        { label: '慢', value: 'slow' },
        { label: '标准', value: 'normal' },
        { label: '快', value: 'fast' },
      ],
    },
    hint: '档位而不是 mm/s（具体速度由机型决定）',
  },
  {
    id: 'media',
    label: '介质',
    group: 'job',
    owner: 'document',
    editor: {
      kind: 'enum',
      value: DEFAULT_JOB.media,
      options: [
        { label: '间隙纸', value: 'gap' },
        { label: '黑标纸', value: 'black-mark' },
        { label: '连续纸', value: 'continuous' },
      ],
    },
    hint: '机器靠什么判断"一张到哪儿了"',
  },
  {
    id: 'feedMm',
    label: '走纸',
    group: 'job',
    owner: 'document',
    editor: { kind: 'mm', value: DEFAULT_JOB.feedMm, min: 0, max: 50, step: 0.5 },
    hint: 'mm；打完额外走纸（撕纸位微调）',
  },
  {
    id: 'dpi',
    label: '分辨率',
    group: 'output',
    owner: 'document',
    editor: {
      kind: 'enum',
      value: DEFAULT_PAPER.dpi,
      options: [
        { label: '203 dpi（8 点/mm）', value: 203 },
        { label: '300 dpi（12 点/mm）', value: 300 },
        { label: '600 dpi（24 点/mm）', value: 600 },
      ],
    },
    hint: '决定设备点数',
  },
]

/* ══════════════════════════════════════════════════════════════════
   查询
   ══════════════════════════════════════════════════════════════════ */

/**
 * 某个组件有哪些参数（按分组顺序）。
 *
 * ⚠️ 符号的**选项**也在这里收口：一维节点只列一维符号、二维只列二维 ——
 * 表和节点定义都从 `barcode.ts` 的符号表生成，不手抄。
 */
export interface ParamQuery {
  /** 这个节点支持哪些符号（一维 / 二维各一份）—— 列在「编码」组里 */
  symbologies?: readonly { id: string; label: string }[]
  /**
   * 按行 id **覆盖默认值**。
   *
   * ⚠️ 存在的理由：参数的"通用默认"和"这个节点的示例"是两回事。
   * 表里的默认是通用值（内容 = 「文本」），而条码要的是**那个符号能编码成功**
   * 的示例内容（`SYMBOLOGIES[].sample`）—— 一个新建的条码节点一放下来就报
   * "编码失败"，用户第一眼看到的就是红色诊断，那是最坏的初体验。
   */
  defaults?: Readonly<Record<string, unknown>>
}

export function paramsFor(component: ParamComponent, options: ParamQuery = {}): ParamSpec[] {
  const chain = componentChain(component)
  return PARAMS.filter((param) => chain.includes(param.owner)).map((param) => {
    let next = param
    if (param.id === 'symbology') {
      const list = options.symbologies ?? []
      if (list.length > 0) {
        next = {
          ...next,
          editor: {
            kind: 'enum' as const,
            value: list[0]!.id,
            options: list.map((spec) => ({ label: spec.label, value: spec.id })),
          },
        }
      }
    }
    const override = options.defaults?.[param.id]
    if (override === undefined) return next
    /* 只覆盖**值**，编辑方式（值域 / 单位 / 选项）还是表里那一份 */
    return { ...next, editor: { ...next.editor, value: override } as ParamEditor }
  })
}

/**
 * 某个组件**要显示哪些组**（按 `PARAM_GROUPS` 的顺序）。
 *
 * 空组不显示。⚠️ 这里与 `sectionOf` / 面板的 `fieldGroup` 是**两层**防护
 * （哪几组该出现、以及空组永不渲染）—— 去掉这一层的过滤，渲染结果**不会变**
 * （下一层兜住了），所以"回退验证"抓不到它；`verify:print` 因此**直接**
 * 断言这个函数的输出（见「二维码只有三组」那几条）。
 */
export function groupsFor(component: ParamComponent, params: readonly ParamSpec[]): ParamGroupSpec[] {
  const used = new Set(params.map((param) => param.group))
  void component
  return PARAM_GROUPS.filter((group) => used.has(group.id))
}

/**
 * **重点参数**（`group: null`）：不进折叠段，直接列在节点上 / 面板顶部。
 *
 * 顺序就是表里的声明顺序 —— 位置 → 尺寸 → 旋转，和用户读的顺序一致。
 */
export function keyParamsFor(params: readonly ParamSpec[]): ParamSpec[] {
  return params.filter((param) => param.group === null)
}

/* ══════════════════════════════════════════════════════════════════
   生成节点行
   ══════════════════════════════════════════════════════════════════ */

/** 参数描述 → 控件（控件 id = 行 id：内联控件和行一一对应） */
export function widgetOf(param: ParamSpec): Widget {
  const editor = param.editor
  const base = { id: param.id, hint: param.hint }
  switch (editor.kind) {
    case 'mm':
      return {
        ...base,
        kind: 'number',
        value: editor.value,
        min: editor.min ?? 0,
        max: editor.max ?? 1000,
        step: editor.step ?? 0.5,
        precision: 2,
        unit: 'mm',
      }
    case 'micron':
      return {
        ...base,
        kind: 'number',
        value: editor.value,
        min: editor.min ?? 100,
        max: editor.max ?? 2000,
        step: editor.step ?? 10,
        precision: 0,
        unit: 'µm',
      }
    case 'number':
      return {
        ...base,
        kind: 'number',
        value: editor.value,
        min: editor.min,
        max: editor.max,
        step: editor.step,
        precision: editor.precision,
        unit: editor.unit,
      }
    case 'text':
      return {
        ...base,
        kind: 'text',
        value: editor.value,
        multiline: editor.multiline,
        rows: editor.rows,
      }
    case 'image':
      return { ...base, kind: 'image', value: editor.value }
    case 'color':
      /*
       * ⚠️ 用内核的**颜色控件**（`ColorWidget`）—— 不是文本框。
       * 第一版写成 `kind: 'text'` 是错的：类型定义里一直有 `color`
       * （`types/widget.ts` 的文件头还写着"颜色是刚需"），
       * 于是在节点上填颜色得手打十六进制（真机反馈："节点上需要用到颜色的参数，
       * 使用颜色的控件，而不是纯文本"）。
       */
      return { ...base, kind: 'color', value: editor.value }
    case 'boolean':
      return { ...base, kind: 'boolean', value: editor.value }
    case 'enum':
      return {
        ...base,
        kind: 'enum',
        value: editor.value,
        options: [...editor.options],
      }
  }
}

/** 参数描述 → 一行（有 socket = 输入行，否则属性行） */
export function rowOf(param: ParamSpec): NodeRow {
  if (param.socket) {
    return {
      kind: 'input',
      id: param.id,
      label: param.label,
      socket: { id: param.id, type: param.socket },
      widget: widgetOf(param),
    }
  }
  return { kind: 'property', id: param.id, label: param.label, widget: widgetOf(param) }
}

/**
 * **把一组参数变成「分组行 + 成员行」**。
 *
 * ⚠️ 分组行的 `match` 由成员 id **生成**（不是手写正则）：
 * 手写的那种迟早和成员列表对不上，而对不上的表现是
 * 「某个参数跑到分组外面去了」—— 没人会去查正则。
 */
export function sectionOf(group: ParamGroupSpec, params: readonly ParamSpec[]): NodeRow[] {
  const members = params.filter((param) => param.group === group.id)
  if (members.length === 0) return []
  const ids = members.map((param) => param.id)
  return [
    {
      kind: 'group',
      id: `g.${group.id}`,
      label: group.label,
      match: `^(${ids.join('|')})$`,
      /* ⚠️ 不写 = 收起（`GroupRow.expanded` 缺省 false）—— 节点高度是手感问题 */
      ...(group.expanded ? { expanded: true } : {}),
    },
    ...members.map(rowOf),
  ]
}

/** 某个组件**完整的行结构**（分组行 + 成员行，按 `PARAM_GROUPS` 的顺序） */
export function rowsForComponent(component: ParamComponent, options: ParamQuery = {}): NodeRow[] {
  const params = paramsFor(component, options)
  /* 重点参数在前（不进段），然后是各个分组段 */
  return [
    ...keyParamsFor(params).map(rowOf),
    ...groupsFor(component, params).flatMap((group) => sectionOf(group, params)),
  ]
}
